import React, {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {Spinner, Row, Col, Button, ButtonGroup, Card} from "reactstrap";
import {VsiBubble, VsiH4, VsiH5, VsiH6, VsiHr} from "../../../../styled/App.styled";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import AtAGlance from "../../../../components/AtAGlance";
import {formatStockChartConfig} from "../../../../utils/Helpers";
import Gauges from "../../../../components/Gauges";
import styles from "../../../../utils/themeStyles";
import Stockchart from "../../../../components/Stockchart";
import AreaChart from "../../../../components/AreaChart";
import {useDataWithRoute, useTitleFromComponent} from "../../../../utils/hooks";
import {vmdkDefs} from "../../../../utils/gridColumnDefs/virtual/datastoreSummary";
import Grid from "../../../../components/Grid";

const vmSummaryAag = [
    {
        field: "vm_name",
        mobile: true,
    },
    {
        field: "datacenter_name",
        mobile: true,
    },
    {
        field: "cluster_name",
        mobile: true,
    },
    {
        field: "esx_host_name",
        diff: true,
        mobile: true,
    },
    {
        field: "operating_sys_name",
        mobile: true,
    }
];

export default function VMSummary({context, title}) {
    const [searchParams] = useSearchParams();
    const { theme, client, periodId } = useVsiAppContext();
    const [chartSeries, setChartSeries] = useState([]);
    const [dollarMetric, setDollarMetric] = useState('cost');
    const [trendSeries, setTrendSeries] = useState([]);
    const [costSeries, setCostSeries] = useState([]);
    const [wasteSeries, setWasteSeries] = useState([]);
    const [detailsSeries, setDetailsSeries] = useState({});
    const [atAGlanceData, setAtAGlanceData] = useState({});
    const [gaugeInfo, setGaugeInfo] = useState({});
    const [allVMDKs, setAllVMDKs] = useState([]);

    const period_id = periodId || searchParams.get('period_id')
    const [data, {vmOverviewIsLoading}] = useDataWithRoute(`/api/v6/report/virtual_vm_overview_proc?client_id=${client.clientId}&period_id=${period_id}&vcenter_name=${encodeURI(searchParams.get('vcenter_name'))}&cluster_name=${encodeURI(searchParams.get('cluster_name'))}&vm_name=${encodeURI(searchParams.get('vm_name'))}&esx_host_name=${encodeURI(searchParams.get('esx_host_name'))}`);
    const [vmdk, {vmStorageIsLoading}] = useDataWithRoute(`/api/v6/report/virtual_vm_vmdk_storage?client_id=${client.clientId}&period_id=${period_id}&vcenter_name=${encodeURI(searchParams.get('vcenter_name'))}&cluster_name=${encodeURI(searchParams.get('cluster_name'))}&vm_name=${encodeURI(searchParams.get('vm_name'))}&esx_host_name=${encodeURI(searchParams.get('esx_host_name'))}`);
    useTitleFromComponent(`Virtual / ${context} / ${title} - ${encodeURI(searchParams.get('vm_name')) || 'N/A'}`);
    const [chartType, setChartType] = useState('utilization');
    const getUtilData = function (dataGrid) {
        let sortedData = dataGrid.sort((a, b) => new Date(a.collection_date) - new Date(b.collection_date));

        let trendChartInfo = (formatStockChartConfig(sortedData, [
            {
                name: 'CPU %',
                field: 'pct_vm_util'
            },
            {
                name: 'Memory %',
                field: 'pct_memory_util'
            },
            {
                name: 'Disk %',
                field: 'pct_disk_util'
            }
        ]));

        let perfChartInfo = (formatStockChartConfig(sortedData, [
            {
                name: 'CPU Ready',
                field: 'cpu_ready'
            },
            {
                name: 'Memory Ballooning',
                field: 'ballooned_memory'
            }
        ]));

        let cpuChartInfo = (formatStockChartConfig(sortedData, [
            {
                name: '# CPUs',
                field: 'num_cpus'
            },
            {
                name: 'Rec. # CPUs',
                field: 'recommended_cpu_size'
            }
        ]));

        let memChartInfo = (formatStockChartConfig(sortedData, [
            {
                name: 'Memory Allocated',
                field: 'memory_allocated'
            },
            {
                name: 'Rec. Memory Allocated',
                field: 'recommended_mem_size'
            }
        ]));

        let diskChartInfo = (formatStockChartConfig(sortedData, [
            {
                name: 'Disk Allocated',
                field: 'disk_allocated'
            },
            {
                name: 'Rec. Disk Allocated',
                field: 'recommended_disk_size'
            }
        ]));

        let costChartInfo = (formatStockChartConfig(sortedData, [
            {
                name: 'CPU $ Cost',
                field: 'cpu_cost'
            },
            {
                name: 'Memory $ Cost',
                field: 'memory_cost'
            },
            {
                name: 'Disk $ Cost',
                field: 'disk_cost'
            }
        ]));

        let wasteChartInfo = (formatStockChartConfig(sortedData, [
            {
                name: 'CPU $ Waste',
                field: 'cpu_waste'
            },
            {
                name: 'Memory $ Waste',
                field: 'memory_waste'
            },
            {
                name: 'Disk $ Waste',
                field: 'disk_waste'
            }
        ]));

        setTrendSeries(trendChartInfo.series);
        setCostSeries(costChartInfo.series);
        setWasteSeries(wasteChartInfo.series);
        setDetailsSeries({cpuChartInfo, memChartInfo, diskChartInfo, perfChartInfo});
    };

    const getAtAGlance = function (data) {
        setAtAGlanceData({
            ...atAGlanceData,
            vm_name: data.vm_name,
            datacenter_name: data.datacenter_name,
            cluster_name: data.cluster_name,
            esx_host_name: data.esx_host_name,
            operating_sys_name: data.os
        });
    };

    const getGauge = function (data) {
        setGaugeInfo({
            ...data,
            cpu_util: (data.vm_util * 100).toLocaleString("en-US", {maximumFractionDigits: 0}),
            memory_util_pct: (data.memory_util_pct * 100).toLocaleString("en-US", {maximumFractionDigits: 0}),
            disk_util_pct: (data.disk_util_pct * 100).toLocaleString("en-US", {maximumFractionDigits: 0}),
            cpu_capacity_score: (data.cpu_capacity_score * 10).toLocaleString("en-US", {maximumFractionDigits: 0}),
            mem_capacity_score: (data.mem_capacity_score * 10).toLocaleString("en-US", {maximumFractionDigits: 0}),
            disk_capacity_score: (data.disk_capacity_score * 10).toLocaleString("en-US", {maximumFractionDigits: 0}),
            cpu_ready: data.cpu_ready,
            ballooned_memory: data.ballooned_memory
        });

        setChartSeries([{
            name: "VSI Capacity Score",
            data: [data.vsi_capacity_score],
            dial: {
                backgroundColor: "gray",
                baseWidth: 5
            },
            pivot: {
                backgroundColor: "gray",
                radius: 6
            },
            dataLabels: {
                borderWidth: 0,
                color: styles[theme].textColor,
                style: {
                    fontSize: '40px'
                }
            },
        }]);
    };

    useEffect(() => {
        const records = data?.recordsets || [];
        getUtilData(records?.[3] || []);
        getGauge(records?.[0]?.[0] || {});
        getAtAGlance(records?.[0]?.[0] || {});

    }, [data]);

    useEffect(() => {
        setAllVMDKs(vmdk?.recordset || []);
    }, [vmdk]);

    const onChartTypeChange = (chartType) => {
        setChartType(chartType);
    }

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>VM Summary - {decodeURI(searchParams.get('vm_name'))}</VsiH4>

            {(vmOverviewIsLoading && vmStorageIsLoading) ? <Spinner color={"primary"}/> : (
                <div>
                    <VsiBubble>
                        <AtAGlance config={vmSummaryAag} data={atAGlanceData} size={"lg"}/>
                    </VsiBubble>
                    <Row>
                        <Col xs={12} md={6} lg={6}>

                            <VsiBubble autobotName='CapacityScoreSpeedometer' autobotInfo={{
                                data: chartSeries,
                                breakdown: {
                                    CPU: gaugeInfo?.cpu_capacity_score,
                                    Memory: gaugeInfo?.mem_capacity_score,
                                    Disk: gaugeInfo?.disk_capacity_score
                                }
                            }}>
                                <VsiH5>Capacity Score</VsiH5>
                                <VsiHr/>
                                <Row><Col> <Gauges series={chartSeries} height={437} title={""} size={'110%'}/>
                                </Col></Row>
                                <Row>
                                    <Col>
                                        <VsiH4
                                            className="text-center">{gaugeInfo?.cpu_capacity_score ?? 'N/A'}</VsiH4>
                                        <VsiH6 className="stat-title text-center">CPU</VsiH6>
                                    </Col>
                                    <Col>
                                        <VsiH4
                                            className="text-center">{gaugeInfo?.mem_capacity_score ?? 'N/A'}</VsiH4>
                                        <VsiH6 className="stat-title text-center">Memory</VsiH6>
                                    </Col>
                                    <Col>
                                        <VsiH4
                                            className="text-center">{gaugeInfo?.disk_capacity_score ?? 'N/A'}</VsiH4>
                                        <VsiH6 className="stat-title text-center">Disk</VsiH6>
                                    </Col>
                                </Row>
                            </VsiBubble>
                        </Col>

                        <Col xs={12} md={6} lg={6}>
                            <VsiBubble noAutobot>
                                <ButtonGroup>
                                    <Button disabled={chartType === 'utilization'}
                                            key={`chart-btn-utilization`}
                                            onClick={() => onChartTypeChange('utilization')}
                                    >Utilization</Button>
                                    <Button disabled={chartType === 'cpus'}
                                            key={`chart-btn-cpus`}
                                            onClick={() => onChartTypeChange('cpus')}
                                    >CPUs</Button>
                                    <Button disabled={chartType === 'memory'}
                                            key={`chart-btn-memory`}
                                            onClick={() => onChartTypeChange('memory')}
                                    >Memory</Button>
                                    <Button disabled={chartType === 'disk'}
                                            key={`chart-btn-disk`}
                                            onClick={() => onChartTypeChange('disk')}
                                    >Disk</Button>
                                    <Button disabled={chartType === 'performance'}
                                            key={`chart-btn-performance`}
                                            onClick={() => onChartTypeChange('performance')}
                                    >Performance</Button>
                                </ButtonGroup>
                                {chartType === 'utilization' &&
                                    <>
                                        <VsiH5>Utilization</VsiH5>
                                        <VsiHr/>
                                        <Row>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{`${gaugeInfo.cpu_util || 'N/A'}%`}</VsiH4>
                                                <VsiH6 className="stat-title text-center">CPU Util</VsiH6>
                                            </Col>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{`${gaugeInfo.memory_util_pct || 'N/A'}%`}</VsiH4>
                                                <VsiH6 className="stat-title text-center">Memory Util</VsiH6>
                                            </Col>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{`${gaugeInfo.disk_util_pct || 'N/A'}%`}</VsiH4>
                                                <VsiH6 className="stat-title text-center">Disk Util</VsiH6>
                                            </Col>
                                        </Row>
                                        <Stockchart series={trendSeries}
                                                    saveParams={{
                                                        vmName: searchParams.get('vm_name'),
                                                        chart: 'util',
                                                    }}
                                                    navDisabled height={400}
                                                    yAxisTitle={'% Utilization'}/>
                                    </>
                                }
                                {chartType === 'cpus' && <>
                                    <VsiH5>CPUs</VsiH5>
                                    <VsiHr/>
                                    <Row>
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`${gaugeInfo.num_cpus ? parseFloat(gaugeInfo.num_cpus.toFixed(2)).toLocaleString(undefined, {minimumFractionDigits: 2}) : 'N/A'}`}</VsiH4>
                                            <VsiH6 className="stat-title text-center">CPUs</VsiH6>
                                        </Col>
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`${gaugeInfo.recommended_cpu_size ? parseFloat(gaugeInfo.recommended_cpu_size.toFixed(2)).toLocaleString(undefined, {minimumFractionDigits: 2}) : 'N/A'}`}</VsiH4>
                                            <VsiH6 className="stat-title text-center">Recommended CPUs</VsiH6>
                                        </Col>
                                        <Col xs={12}>
                                            <Stockchart series={detailsSeries?.cpuChartInfo?.series}
                                                        saveParams={{
                                                            vmName: searchParams.get('vm_name'),
                                                            chart: 'cores',
                                                        }}
                                                        navDisabled
                                                        height={400} yAxisTitle={'Cores'}/>
                                        </Col>
                                    </Row>
                                </>
                                }

                                {chartType === 'memory' &&
                                    <>
                                        <VsiH5>Memory</VsiH5>
                                        <VsiHr/>
                                        <Row>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{`${gaugeInfo.memory_allocated || 'N/A'}`}</VsiH4>
                                                <VsiH6 className="stat-title text-center">Memory Allocated (GiB)</VsiH6>
                                            </Col>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{`${Math.round(gaugeInfo.recommended_mem_size / 1000) || 'N/A'}`}</VsiH4>
                                                <VsiH6 className="stat-title text-center">Recommended Allocation
                                                    (GiB)</VsiH6>
                                            </Col>
                                            <Col xs={12}>
                                                <Stockchart series={detailsSeries?.memChartInfo?.series} navDisabled
                                                            height={400} yAxisTitle={'GiB'}/>
                                            </Col>
                                        </Row>
                                    </>
                                }

                                {chartType === 'disk' &&
                                    <>
                                        <VsiH5>Disk</VsiH5>
                                        <VsiHr/>
                                        <Row>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{gaugeInfo.disk_allocated?.toLocaleString("en-US") || 'N/A'}</VsiH4>
                                                <VsiH6 className="stat-title text-center">Disk Allocated (GiB)</VsiH6>
                                            </Col>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{gaugeInfo.recommended_disk_size?.toLocaleString("en-US") || 'N/A'}</VsiH4>
                                                <VsiH6 className="stat-title text-center">Recommended Allocation
                                                    (GiB)</VsiH6>
                                            </Col>
                                            <Col xs={12}>
                                                <Stockchart series={detailsSeries?.diskChartInfo?.series} navDisabled
                                                            height={400} yAxisTitle={'GiB'}/>
                                            </Col>

                                        </Row>
                                    </>
                                }

                                {chartType === 'performance' &&
                                    <>
                                        <VsiH5>Performance</VsiH5>
                                        <VsiHr/>
                                        <Row>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{gaugeInfo.cpu_ready?.toLocaleString("en-US") || 'N/A'}</VsiH4>
                                                <VsiH6 className="stat-title text-center">CPU Ready</VsiH6>
                                            </Col>
                                            <Col>
                                                <VsiH4
                                                    className="text-center">{gaugeInfo.ballooned_memory?.toLocaleString("en-US") || 'N/A'}</VsiH4>
                                                <VsiH6 className="stat-title text-center">Memory Ballooning
                                                    (GiB)</VsiH6>
                                            </Col>
                                            <Col xs={12}>
                                                <Stockchart series={detailsSeries?.perfChartInfo?.series} navDisabled
                                                            height={400} yAxisTitle={'GiB'}/>
                                            </Col>

                                        </Row>
                                    </>
                                }
                            </VsiBubble>

                        </Col>
                    </Row>
                    <Row><Col xs={12} md={6} lg={6}><VsiBubble>
                        <Grid
                            data={allVMDKs}
                            columnDefs={vmdkDefs}
                            height={455}
                        />
                    </VsiBubble>
                    </Col>
                        <Col xs={12} md={6} lg={6}>

                            <VsiBubble autobotName='SavingsAreaChart' unit={dollarMetric} autobotInfo={{
                                data: dollarMetric === 'waste' ? wasteSeries : costSeries,
                                yAxisTitle: dollarMetric === 'waste' ? '$ Waste' : '$ Cost',
                                breakdown: dollarMetric === 'waste' ?
                                    {
                                        Cpu_waste: gaugeInfo?.monthly_cpu_waste,
                                        Memory_waste: gaugeInfo?.monthly_memory_waste,
                                        Disk_waste: gaugeInfo?.monthly_disk_waste
                                    }
                                    : {
                                        Cpu_cost: gaugeInfo?.monthly_cpu_cost,
                                        Memory_cost: gaugeInfo?.monthly_memory_cost,
                                        Disk_cost: gaugeInfo?.monthly_disk_cost
                                    },
                                title: 'Savings'
                            }}>
                                {dollarMetric === 'waste' ? <>
                                    <div className={"d-flex"}>
                                        <VsiH5 className={"pe-2"}>Savings</VsiH5>
                                        <ButtonGroup size={"sm"} style={{height: "100%"}}>
                                            <Button disabled={dollarMetric === 'cost'} size="sm"
                                                    onClick={() => setDollarMetric('cost')}>Cost</Button>
                                            <Button disabled={dollarMetric === 'waste'} size="sm"
                                                    onClick={() => setDollarMetric('waste')}>Waste</Button>
                                        </ButtonGroup>
                                    </div>
                                    <VsiHr/>
                                    <Row>
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`$${gaugeInfo?.monthly_cpu_waste.toLocaleString?.() ?? "N/A"}`}
                                                <i className="fas fa-trash-alt ml-1"
                                                   style={{fontSize: "10px"}}/></VsiH4>
                                            <VsiH6 className="stat-title text-center">CPU Waste</VsiH6>
                                        </Col>
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`$${gaugeInfo?.monthly_memory_waste.toLocaleString?.() ?? 'N/A'}`}
                                                <i className="fas fa-trash-alt ml-1"
                                                   style={{fontSize: "10px"}}/></VsiH4>
                                            <VsiH6 className="stat-title text-center">Memory Waste</VsiH6>
                                        </Col>
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`$${gaugeInfo?.monthly_disk_waste.toLocaleString?.() ?? 'N/A'}`}
                                                <i className="fas fa-trash-alt ml-1"
                                                   style={{fontSize: "10px"}}/></VsiH4>
                                            <VsiH6 className="stat-title text-center">Disk Waste</VsiH6>
                                        </Col>
                                    </Row>
                                    <AreaChart height={422} series={wasteSeries} navDisabled yAxisTitle='$ Waste'/>
                                </> : <>
                                    <div className={"d-flex"}>
                                        <VsiH5 className={"pe-2"}>Savings</VsiH5>
                                        <ButtonGroup size={"sm"} style={{height: "100%"}}>
                                            <Button disabled={dollarMetric === 'cost'} size="sm"
                                                    onClick={() => setDollarMetric('cost')}>Cost</Button>
                                            <Button disabled={dollarMetric === 'waste'} size="sm"
                                                    onClick={() => setDollarMetric('waste')}>Waste</Button>
                                        </ButtonGroup>
                                    </div>
                                    <VsiHr/>
                                    <Row className="pt-2">
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`$${gaugeInfo?.monthly_cpu_cost?.toLocaleString?.() ?? 'N/A'}`}
                                                <i className="fas fa-coins ml-1" style={{fontSize: "10px"}}/></VsiH4>
                                            <VsiH6 className="stat-title text-center">CPU Cost</VsiH6>
                                        </Col>
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`$${gaugeInfo?.monthly_memory_cost?.toLocaleString?.() ?? 'N/A'}`}
                                                <i className="fas fa-coins ml-1" style={{fontSize: "10px"}}/></VsiH4>
                                            <VsiH6 className="stat-title text-center">Memory Cost</VsiH6>
                                        </Col>
                                        <Col>
                                            <VsiH4
                                                className="text-center">{`$${gaugeInfo?.monthly_disk_cost?.toLocaleString?.() ?? 'N/A'}`}
                                                <i className="fas fa-coins ml-1" style={{fontSize: "10px"}}/></VsiH4>
                                            <VsiH6 className="stat-title text-center">Disk Cost</VsiH6>
                                        </Col>
                                    </Row>
                                    <AreaChart height={422} series={costSeries} navDisabled yAxisTitle='$ Cost'/>
                                </>}
                            </VsiBubble>
                        </Col></Row>

                </div>
            )}
        </Card>
    );
}
